/**
 * `aria-keyshortcuts` string builder.
 *
 * Produces a WAI-ARIA `aria-keyshortcuts` value for a binding id so screen
 * readers can announce it. A `mod` chord emits both `Meta+…` and `Control+…`
 * variants (space-separated) since the same binding fires on either platform.
 * Returns `''` for unknown ids so callers can spread it unconditionally.
 */

import { keybindingRegistry } from './keybindingRegistry';
import type { KeyChord, KeybindingId } from './keybinding.types';

function ariaKeyName(key: string): string {
  if (key === ' ') return 'Space';
  return key.length === 1 ? key.toUpperCase() : key;
}

function ariaChord(chord: KeyChord): string[] {
  const mods: string[] = [];
  if (chord.alt) mods.push('Alt');
  if (chord.shift) mods.push('Shift');
  const combo = [...mods, ariaKeyName(chord.key)].join('+');
  if (chord.mod) return [`Meta+${combo}`, `Control+${combo}`];
  return [combo];
}

export function ariaKeyshortcutsFor(id: KeybindingId): string {
  const binding = keybindingRegistry.byId(id);
  if (!binding) return '';
  return binding.chords.flatMap(ariaChord).join(' ');
}
